import { motion } from "framer-motion"
import { AlertCircle } from "lucide-react"
import { useNavigate } from "react-router"
import { Button } from "@web/components/ui/button"
import { Card, CardContent } from "@web/components/ui/card"
import { cardVariants, fadeInVariants } from "@web/styles/motion-variants"
import { useLanguage } from "@web/lib/contexts/language-context"

type ErrorDisplayProps = {
	message: string
	onRetry?: () => void
	is404?: boolean
	isGeolocationError?: boolean
}

export function ErrorDisplay({ message, onRetry, is404, isGeolocationError }: ErrorDisplayProps) {
	const navigate = useNavigate()
	const { t } = useLanguage()

	const getIconColor = () => {
		if (isGeolocationError) return "bg-orange-100 text-orange-600"
		return "bg-red-100 text-red-600"
	}

	const getTitle = () => {
		if (isGeolocationError) return t.errorsDisplay.geolocationRequired
		if (is404) return t.errorsDisplay.sealNotFound
		return t.errorsDisplay.verificationError
	}

	return (
		<motion.div
			className="flex min-h-screen flex-col bg-gray-50"
			initial="hidden"
			animate="visible"
			variants={fadeInVariants}>
			<main className="flex flex-1 flex-col items-center p-4 md:p-8">
				<motion.div className="w-full max-w-xl" variants={cardVariants}>
					<Card className="w-full overflow-hidden rounded-xl shadow-lg">
						<CardContent className="flex flex-col items-center justify-center gap-4 p-8">
							<motion.div
								className={`flex h-16 w-16 items-center justify-center rounded-full ${getIconColor()}`}
								initial={{ scale: 0 }}
								animate={{ scale: 1 }}
								transition={{
									type: "spring",
									stiffness: 300,
									damping: 20,
									delay: 0.2,
								}}>
								<AlertCircle className="h-8 w-8" />
							</motion.div>
							<motion.h2
								className="text-center font-bold text-xl"
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: 0.3 }}>
								{getTitle()}
							</motion.h2>
							<motion.p
								className="text-center text-gray-600"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								transition={{ delay: 0.4 }}>
								{message}
							</motion.p>

							{isGeolocationError && (
								<motion.div
									className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg"
									initial={{ opacity: 0, y: 10 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ delay: 0.5 }}>
									<h3 className="font-semibold text-orange-800 mb-2">{t.errorsDisplay.howToEnableGeolocation}</h3>
									<ul className="text-sm text-orange-700 space-y-1">
										<li>• {t.errorsDisplay.step1}</li>
										<li>• {t.errorsDisplay.step2}</li>
										<li>• {t.errorsDisplay.step3}</li>
									</ul>
								</motion.div>
							)}

							{onRetry && (
								<motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
									<Button onClick={onRetry} className="mt-4 bg-primary">
										{t.errorsDisplay.retry}
									</Button>
								</motion.div>
							)}
							<motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}>
								<Button variant="outline" onClick={() => navigate("/")} className="mt-2">
									{t.errorsDisplay.backHome}
								</Button>
							</motion.div>
						</CardContent>
					</Card>
				</motion.div>
			</main>
		</motion.div>
	)
}
